import type {Flow_AbstractComponent} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import {classify} from 'src/utils/index';
import useI18n from 'src/hooks/useI18n';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {PAGE_NAME_LIST, duotoneIconStyle} from 'src/utils/app-shell';
import flow from 'lodash/flow';
import {provideHooks} from 'src/flux/provideHooks';
import {withWindowWidth} from 'src/flux/responsive';
import {fluxify} from 'src/flux/component';
import {getAgency} from 'src/action-creators/agency';
import {getAllEntities} from 'src/action-creators/ats-entities';
import {usePageTitle} from 'src/contexts/page-title-context';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  TEXT_COLORS,
  TitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Separator} from '@spaced-out/ui-design-system/lib/components/Separator';
import {CommonTopNavButtons} from 'src/components/app-shell/common-top-nav-buttons';
import css from './page-title.css';


type ClassNames = Readonly<{
  wrapper?: string;
  topSlot?: string;
  rightSlot?: string;
}>;

export type PageTitleProps = {
  classNames?: ClassNames;
  children?: React.ReactNode;
  pageNameKey?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  rightSlotComponent?: React.ReactNode;
};

export type TabSlotProps = {
  children?: React.ReactNode;
  className?: string;
};

const dependencies = {
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  required: ({dispatch}) => dispatch(getAgency()),
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  deferred: ({dispatch}) => dispatch(getAllEntities()),
} as const;

// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<HTMLDivElement & RefAttributes<PageTitleProps>>' is not assignable to type 'AbstractComponent<PageTitleProps, HTMLDivElement>'.
const PageTitleComponent: Flow_AbstractComponent<
  PageTitleProps,
  HTMLDivElement
> = React.forwardRef<PageTitleProps, HTMLDivElement>(
  // @ts-expect-error - TS2345 - Argument of type '(props: PageTitleProps, ref: ForwardedRef<PageTitleProps>) => Element | null' is not assignable to parameter of type 'ForwardRefRenderFunction<PageTitleProps, HTMLDivElement>'.
  (props: PageTitleProps, ref) => {
    const {labelI18nInstance} = useI18n();
    const t = labelI18nInstance.t;
    const {pageTitleState: contextState} = usePageTitle();
    const mergedProps = {...contextState, ...props} as const;
    const {
      classNames,
      pageNameKey,
      showBackButton,
      onBack,
      tabSlotComponent,
      customPageNameComponent,
      rightSlotComponent,
      hidePageTitle,
    } = mergedProps;

    const {isExtension, isClientIframe} = useEnvironmentContext();

    const handleBack = () => {
      if (onBack) {
        onBack();
      } else if (window.history.length > 1) {
        // This is a temporary fix, this needs to be practice going forward
        // to handle history.back in the respective components
        window.history.back();
      }
    };

    return !hidePageTitle ? (
      <div
        data-testid="PageTitle"
        className={classify(css.pageTitleWrapper, classNames?.wrapper, {
          [css.pageTitleWrapperWithBottomSlot]: !!tabSlotComponent,
        })}
        // @ts-expect-error - TS2322 - Type 'ForwardedRef<PageTitleProps>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        ref={ref}
      >
        {/* @ts-expect-error - TS2339 - Property 'topSlot' does not exist on type 'Readonly<{ wrapper?: string | undefined; leftSlot?: string | undefined; rightSlot?: string | undefined; }> | Readonly<{ wrapper?: string | undefined; topSlot?: string | undefined; rightSlot?: string | undefined; }>'. */}
        <div className={classify(css.topSlot, classNames?.topSlot)}>
          <div className={css.headerWithBackBtn}>
            {showBackButton && (
              <Tooltip
                title={t('Navigate_Back', 'Navigate Back')}
                delayMotionDuration="slow"
                placement="top-start"
              >
                <Button
                  onClick={handleBack}
                  type="tertiary"
                  iconLeftName="chevron-left"
                  ariaLabel={t('Navigate_Back_Button', 'Navigate Back Button')}
                />
              </Tooltip>
            )}
            {customPageNameComponent ? (
              customPageNameComponent
            ) : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ dashboard: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; engage: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; journeys: { ...; }; ... 19 more ...; broadcast: { ...; }; }>'.
            pageNameKey && PAGE_NAME_LIST[pageNameKey] ? (
              <PageName>
                {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ dashboard: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; engage: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; journeys: { ...; }; ... 19 more ...; broadcast: { ...; }; }>'. */}
                <TitleMedium>{PAGE_NAME_LIST[pageNameKey].title} </TitleMedium>
                <Icon
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ dashboard: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; engage: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; journeys: { ...; }; ... 19 more ...; broadcast: { ...; }; }>'.
                  type={PAGE_NAME_LIST[pageNameKey].iconType}
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ dashboard: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; engage: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; journeys: { ...; }; ... 19 more ...; broadcast: { ...; }; }>'.
                  name={PAGE_NAME_LIST[pageNameKey].iconName}
                  size="medium"
                  color={TEXT_COLORS.primary}
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ dashboard: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; engage: { title: string; iconName: string; iconType: string; iconSwapOpacity: boolean; }; journeys: { ...; }; ... 19 more ...; broadcast: { ...; }; }>'.
                  swapOpacity={PAGE_NAME_LIST[pageNameKey].iconSwapOpacity}
                  style={duotoneIconStyle}
                />
              </PageName>
            ) : null}
          </div>
          <div
            className={classify(css.rightContainer, {
              [css.rightContainerWithRightSlot]: null,
            })}
          >
            <div className={css.buttonsSlot}>
              {!tabSlotComponent && !!rightSlotComponent && rightSlotComponent}
              <Separator
                orientation="vertical"
                classNames={{wrapper: css.separator}}
              />
              {!(isExtension || isClientIframe) && <CommonTopNavButtons />}
            </div>
          </div>
        </div>
        {!!tabSlotComponent && (
          <div className={css.bottomSlot}>
            {tabSlotComponent}
            <div className={classify(css.rightSlot, classNames?.rightSlot)}>
              {rightSlotComponent ? rightSlotComponent : null}
            </div>
          </div>
        )}
      </div>
    ) : null;
  },
);

export const PageTitle = flow(
  withWindowWidth,
  // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type '(arg1: T & { store: IndexStore; params: { [key: string]: string; }; }) => unknown'.
  fluxify({}, {pure: true}),
  provideHooks(dependencies),
)(PageTitleComponent) as React.ComponentType<PageTitleProps>;
